import { CookieSettings } from 'types/ProductDetailsType';

export type RegionalConsentDefault = 'granted' | 'denied';

// The brand dashboard's "European Union" countries entry.
export const EU_CONSENT_REGION_TOKEN = 'EU';

// Every visitor country the 'EU' countries entry covers: the EU-27, the other
// EEA members (IS, LI, NO), the UK, Switzerland, and the EU territories that
// geolocate under their own ISO codes (the French outermost regions and the
// Aland Islands, which are part of Finland).
export const EU_CONSENT_REGION: readonly string[] = [
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE',
  'IS',
  'LI',
  'NO',
  'GB',
  'CH',
  'GF',
  'GP',
  'MQ',
  'RE',
  'YT',
  'MF',
  'AX',
];

export type ConsentRegionInput = {
  cookieSettings?: CookieSettings | null;
  countryCode?: string | null;
  regionCode?: string | null;
  isEmbedded?: boolean;
};

export type ConsentRegion = {
  // The brand's banner must be shown to this visitor.
  bannerRequired: boolean;
  // What applies until the visitor decides (or after a dismissal).
  defaultConsent: RegionalConsentDefault;
  isEuConsentRegion: boolean;
  geoKnown: boolean;
  // The banner may be closed without a decision (close X).
  dismissible: boolean;
};

const asCode = (value: unknown): string | undefined =>
  typeof value === 'string' && value ? value : undefined;

// List membership only. A bare 'EU' country code is outside the region,
// though it still matches the 'EU' countries entry by its own code.
const isInEuConsentRegion = (countryCode?: string): boolean =>
  !!countryCode && EU_CONSENT_REGION.includes(countryCode);

// Only 'granted' and 'denied' are valid; anything else (absent, malformed)
// falls back to the list's default.
const resolveListDefault = (
  value: unknown,
  fallback: RegionalConsentDefault
): RegionalConsentDefault =>
  value === 'granted' || value === 'denied' ? value : fallback;

const matchesCountryEntry = (
  entryValue: unknown,
  countryCode: string
): boolean =>
  entryValue === countryCode ||
  (entryValue === EU_CONSENT_REGION_TOKEN && isInEuConsentRegion(countryCode));

// Single source of truth for whether a brand's banner applies to a visitor
// and which default applies until they decide. Used by BOTH the CookieBanner
// and the event-level snapshot (utils/getCookieConsent.ts) so the two can
// never disagree. Pure: the caller supplies geo and the iframe check.
// - Privacy off (no settings / not enabled): no banner, default granted.
// - Unknown geo: banner + denied (fail closed), except inside an iframe
//   (brand-dashboard admin registration / preview), where it stays off.
// - The 'EU' countries entry expands to EU_CONSENT_REGION; any other entry
//   matches its own country code only (no continent matching).
// - usaStates entries match US visitors by region code.
// - A match uses that list's default; when both lists match, denied wins.
export const resolveConsentRegion = ({
  cookieSettings,
  countryCode,
  regionCode,
  isEmbedded = false,
}: ConsentRegionInput): ConsentRegion => {
  const country = asCode(countryCode);
  const region = asCode(regionCode);
  const geoKnown = !!country;
  const isEuConsentRegion = isInEuConsentRegion(country);
  const noBanner: ConsentRegion = {
    bannerRequired: false,
    defaultConsent: 'granted',
    isEuConsentRegion,
    geoKnown,
    dismissible: false,
  };

  try {
    if (!cookieSettings?.enabled) return noBanner;

    if (!country) {
      if (isEmbedded) return noBanner;
      return {
        bannerRequired: true,
        defaultConsent: 'denied',
        isEuConsentRegion,
        geoKnown,
        dismissible: false,
      };
    }

    const countries = Array.isArray(cookieSettings.countries)
      ? cookieSettings.countries
      : [];
    const usaStates = Array.isArray(cookieSettings.usaStates)
      ? cookieSettings.usaStates
      : [];

    const countryMatch = countries.some((entry) =>
      matchesCountryEntry(entry?.value, country)
    );
    const stateMatch =
      country === 'US' &&
      !!region &&
      usaStates.some((entry) => entry?.value === region);

    if (!countryMatch && !stateMatch) return noBanner;

    const matchedDefaults: RegionalConsentDefault[] = [];
    if (countryMatch) {
      matchedDefaults.push(
        resolveListDefault(cookieSettings.countriesDefaultConsent, 'denied')
      );
    }
    if (stateMatch) {
      matchedDefaults.push(
        resolveListDefault(cookieSettings.usaStatesDefaultConsent, 'granted')
      );
    }

    return {
      bannerRequired: true,
      defaultConsent: matchedDefaults.includes('denied') ? 'denied' : 'granted',
      isEuConsentRegion,
      geoKnown,
      dismissible: !isEuConsentRegion,
    };
  } catch (error) {
    console.error('CONSENT REGION RESOLUTION FAILED: ', error);
    // Malformed settings: treat like unknown geo (fail closed).
    return {
      bannerRequired: !isEmbedded,
      defaultConsent: isEmbedded ? 'granted' : 'denied',
      isEuConsentRegion,
      geoKnown,
      dismissible: false,
    };
  }
};
